/**
 * SitePick front — 実物確認画面のカラー切替（`/sites/{slug}/live/`）。docs/07 §13。
 *
 * iframe は sandbox（allow-same-origin なし）なので、親から中の CSS は触れない。
 * やり取りは postMessage だけ:
 *  - デモ → 親: `sitepick:demo-color:ready`（色の案の一覧。ページを読み込むたびに届く）
 *  - 親 → デモ: `sitepick:demo-color:set`（選んだ案の key）
 * デモ内でページを移るたびに ready が届くので、そのつど親の選択を返して同じ色を保つ。
 * （iframe の name は sandbox の iframe の中へ届かないので使わない。Chrome で実測 2026-10-21）
 *
 * 案を用意していない商品からは知らせが来ないので、ボタンは出ない（hidden のまま）。
 * 価格・在庫には一切関わらない。
 */

import { previewColor } from "./analytics.js";
import { storedColor, storeColor } from "./color-store.js";

const KEY_RE = /^[a-z][a-z0-9-]{0,23}$/;
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const MAX_COLORS = 5;

/**
 * デモから届いた一覧を検査して、使える形だけ返す（届いた値は信用しない）。
 *
 * @param {unknown} raw
 * @returns {Array<{key: string, label: string, swatch: string[]}>}
 */
function sanitizeColors(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = [];
  const seen = new Set();
  for (const c of raw.slice(0, MAX_COLORS)) {
    if (!c || typeof c.key !== "string" || typeof c.label !== "string" || !Array.isArray(c.swatch)) {
      continue;
    }
    const key = c.key;
    if (!(key === "base" || KEY_RE.test(key)) || seen.has(key)) {
      continue;
    }
    const swatch = c.swatch.filter((s) => typeof s === "string" && HEX_RE.test(s)).slice(0, 3);
    if (!swatch.length) {
      continue;
    }
    seen.add(key);
    out.push({ key, label: c.label.slice(0, 12), swatch });
  }
  // 標準（base）が無い一覧は受け付けない（元の配色へ戻れなくなる）。
  return out.some((c) => c.key === "base") && out.length >= 2 ? out : [];
}

/**
 * @param {Document} doc
 * @returns {void}
 */
export function initLiveColors(doc) {
  const root = doc.querySelector("[data-sp-preview]");
  const group = doc.querySelector(".js-sp-live-colors");
  const iframe = root ? root.querySelector(".p-live__iframe") : null;
  const list = group ? group.querySelector("[data-sp-colors-list]") : null;
  if (!root || !group || !list || !(iframe instanceof HTMLIFrameElement)) {
    return;
  }

  let itemId = "";
  try {
    const parsed = JSON.parse(root.getAttribute("data-sp-item") || "{}");
    itemId = parsed && parsed.item_id ? String(parsed.item_id) : "";
  } catch {
    itemId = "";
  }

  let current = "base";
  let rendered = "";
  // 一覧カード・商品詳細で選んだ色（color-store.js）。最初に一覧が届いたときに 1 回だけ当てる（計測はしない）。
  const postId = group.getAttribute("data-sp-color-id") || "";
  let pending = storedColor(postId);

  const send = (key) => {
    if (!iframe.contentWindow) {
      return;
    }
    // sandbox の iframe は opaque origin なので targetOrigin は指定できない。送るのは案の key だけ。
    iframe.contentWindow.postMessage({ type: "sitepick:demo-color:set", key }, "*");
  };

  const markActive = () => {
    list.querySelectorAll(".js-sp-live-color").forEach((btn) => {
      const active = btn.getAttribute("data-sp-color") === current;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const choose = (key, track = true) => {
    if (key === current) {
      return;
    }
    current = key;
    send(key);
    markActive();
    storeColor(postId, key);
    if (track) {
      previewColor(itemId, key, "live");
    }
  };

  const render = (colors) => {
    const signature = colors.map((c) => c.key).join(",");
    if (signature === rendered) {
      return;
    }
    rendered = signature;
    list.textContent = "";
    colors.forEach((c) => {
      const btn = doc.createElement("button");
      btn.type = "button";
      btn.className = "c-color-toggle__btn js-sp-live-color";
      btn.setAttribute("data-sp-color", c.key);
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("aria-label", `${c.label}で表示${c.key === "base" ? "（標準）" : ""}`);
      btn.title = c.key === "base" ? `${c.label}（標準）` : c.label;
      const chip = doc.createElement("span");
      chip.className = "c-color-toggle__chip";
      chip.setAttribute("aria-hidden", "true");
      // 見本の色は検査済みの #hex だけ。カスタムプロパティで渡し、見た目は CSS が決める。
      chip.style.setProperty("--sp-chip-1", c.swatch[0]);
      chip.style.setProperty("--sp-chip-2", c.swatch[1] || c.swatch[0]);
      btn.appendChild(chip);
      btn.addEventListener("click", () => choose(c.key));
      list.appendChild(btn);
    });
    group.hidden = false;
    markActive();
  };

  window.addEventListener("message", (e) => {
    if (e.source !== iframe.contentWindow) {
      return;
    }
    const d = e.data;
    if (!d || d.type !== "sitepick:demo-color:ready") {
      return;
    }
    const colors = sanitizeColors(d.colors);
    if (!colors.length) {
      return;
    }
    render(colors);
    if (pending) {
      const want = pending;
      pending = "";
      if (colors.some((c) => c.key === want)) {
        choose(want, false);
        return;
      }
    }
    // デモ内でページを移ると元の配色で読み込まれるので、親の選択を返す。
    if (d.current !== current) {
      send(colors.some((c) => c.key === current) ? current : "base");
    }
  });
}
